import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usersAPI, clientsAPI } from '../services/api';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  LockReset as ResetIcon,
  Block as DisableIcon,
  CheckCircle as EnableIcon,
} from '@mui/icons-material';

const Users = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'client',
    clientId: '',
    enabled: true,
  });

  const ROLE_OPTIONS = [
    { value: 'admin', label: t('auth.role.admin') },
    { value: 'operator', label: t('auth.role.operator') },
    { value: 'client', label: t('auth.role.client') },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, clientsRes] = await Promise.all([
        usersAPI.getAll(),
        clientsAPI.getAll(),
      ]);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
      setError('');
    } catch (err) {
      setError(t('users.failedToLoad'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: '',
        role: user.role,
        clientId: user.clientId || '',
        enabled: user.enabled,
      });
    } else {
      setEditingUser(null);
      setFormData({ username: '', password: '', role: 'client', clientId: '', enabled: true });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({ ...formData, [name]: name === 'enabled' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (!data.clientId) delete data.clientId;
      if (editingUser && !data.password) delete data.password;

      if (editingUser) {
        await usersAPI.update(editingUser.id, data);
      } else {
        await usersAPI.create(data);
      }
      handleCloseModal();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || t('users.failedToSave'));
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await usersAPI.update(user.id, { enabled: !user.enabled });
      fetchData();
    } catch (err) {
      setError(t('users.failedToUpdateStatus'));
    }
  };

  const handleResetPassword = (user) => {
    setUserToReset(user);
    setNewPassword('');
    setResetPasswordOpen(true);
  };

  const handleConfirmResetPassword = async () => {
    try {
      await usersAPI.resetPassword(userToReset.id, newPassword);
      setResetPasswordOpen(false);
      setUserToReset(null);
      setNewPassword('');
    } catch (err) {
      setError(t('users.failedToReset'));
    }
  };

  const availableClients = clients.filter((c) =>
    !c.user?.id || c.user?.id === editingUser?.id
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{t('users.title')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
          {t('users.addUser')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600 }}>{t('users.username')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('users.role')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('users.linkedClient')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('users.status')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('users.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>
                  <Chip
                    label={t(`auth.role.${user.role}`)}
                    color={user.role === 'admin' ? 'error' : user.role === 'operator' ? 'primary' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{user.clientInfo?.companyName || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={user.enabled ? t('common.enabled') : t('common.disabled')}
                    color={user.enabled ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={t('common.edit')}>
                    <IconButton onClick={() => handleOpenModal(user)} size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('users.resetPassword')}>
                    <IconButton onClick={() => handleResetPassword(user)} size="small">
                      <ResetIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={user.enabled ? t('common.disable') : t('common.enable')}>
                    <IconButton
                      onClick={() => handleToggleStatus(user)}
                      size="small"
                      color={user.enabled ? 'error' : 'success'}
                    >
                      {user.enabled ? <DisableIcon /> : <EnableIcon />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* User Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? t('users.editUser') : t('users.addNew')}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField fullWidth label={t('users.username')} name="username"
              value={formData.username} onChange={handleChange} required margin="normal"
              disabled={!!editingUser} />
            <TextField fullWidth
              label={editingUser ? t('users.newPasswordEdit') : t('users.password')}
              name="password" type="password"
              value={formData.password} onChange={handleChange}
              required={!editingUser} margin="normal" />
            <FormControl fullWidth margin="normal">
              <InputLabel>{t('users.role')}</InputLabel>
              <Select name="role" value={formData.role} onChange={handleChange}
                label={t('users.role')} required>
                {ROLE_OPTIONS.map((role) => (
                  <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {formData.role === 'client' && (
              <FormControl fullWidth margin="normal">
                <InputLabel>{t('users.linkedClient')}</InputLabel>
                <Select name="clientId" value={formData.clientId} onChange={handleChange}
                  label={t('users.linkedClient')}>
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  {availableClients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.companyName} ({client.clientNumber})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControlLabel
              control={<Switch name="enabled" checked={formData.enabled} onChange={handleChange} />}
              label={t('common.enabled')} sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">
              {editingUser ? t('common.update') : t('common.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onClose={() => setResetPasswordOpen(false)}>
        <DialogTitle>{t('users.resetPasswordTitle')}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t('users.resetPasswordFor')} <strong>{userToReset?.username}</strong>
          </Typography>
          <TextField fullWidth label={t('users.newPasswordLabel')} type="password"
            value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
            margin="normal" required />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleConfirmResetPassword} variant="contained" disabled={!newPassword}>
            {t('users.resetPassword')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
